/* eslint-disable camelcase */
export interface ObjectID {
  toHexString: () => string
}

export enum DedupeType {
  CUSTOM = 'custom',
  ORG = 'organization'
}

export interface LoginRequest {
  auth_type: 'password' | 'saml',
  email: string,
  password: string,
  next?: string | false
}
export interface Tap<T> {
  _id: ObjectID,
  name: string,
  createdAt: number,
  updatedAt: number,
  count: number,
  payload: T
}

export interface Workspace {
  type: 'dev' | 'client' | 'inactive' | 'testing' | 'marketing' | 'betas' | 'sales' | 'dev' | 'client_partner'
  icon: string,
  reports?: Projects,
  slack: SlackIntegrationConfig,
  products: string,
  lastCompletedDropAt: number,
  organization: Relation,
  account_manager: string,
  clientid: string,
  configs: GeneralConfig,
  priority: string[],
  slack_channel?: string,
  tap_audiences: Audiences
}

export interface Segment {
  lucid: Criteria
  custom: string | false
  countries?: any
}

export interface Criteria {
  [key: string]: string | any[]
}
export interface FormattedAudienceForRR {
  name: string
  segment: Segment
}

export interface Organization {
  type: 'organization',
  icon: string,
  domains: string[],
  capacity: string,
  account: {
    id: string
  },
  tap_audiences: {
    [x: string]: {
      label: string,
      description: string,
      createdAt: number
    }
  },
  priority: {
    list: {
      testId: number,
      workspaceId: string
    }[],
    uuid: string
  }
}

export interface Account {
  type: 'account',
  icon: string,
}

export interface UserWorkspacePreference {
  [key: string]: string
}
export interface UserPreferences {
  [key: string]: UserWorkspacePreference
}

export interface UserPayload {
  company: string,
  primary_account: string,
  accounts: string[],
  firstname: string,
  lastname?: string,
  show_tutorial: boolean,
  permissions: {
    [key: string]: 'basic_user' | 'workspace_admin' | 'organization_admin'
  },
  task_types? : string[],
  payment_details: any,
  tester?: 'active'
  preferences: UserPreferences
}

export interface UserAuths {
  email: {
    uid: string,
    password_hash: string
  }
}

export interface UserTestCreationRecord {
  tap_id: string,
  drop: string
}

export interface User {
  _id: string,
  username: null,
  type?: 'admin',
  payload: UserPayload
  auths: UserAuths,
  created_at?: number,
  updated_at?: number,
  created?: {
    drops?: UserTestCreationRecord[],
    taps?: string[]
  }
}

export interface Projects {
  [id: string]: Project
}
export interface Project {
  id: string,
  ids: string[],
  title: string,
  desc: string,
  type: string,
  assets?: Record<string, Prototype>,
  audiences?: string[]
  clientid?: string,
  updatedAt?: number, // new
  uid?: number // deprecated,
  sections?: Section[] // deprecated,
  usedTemplate?: boolean // deprecated,
  creator?: string // deprecated ?,
  accessList?: [] // deprecated,
  hidden?: boolean // new
}

export enum TestStatus {
  InReview='In Review',
  InProgress='In Progress',
  Draft='Draft',
  Complete='Complete',
  // deprecated statuses
  InTesting='In Testing',
  InDesign='In Design'
}

export const TestStatusLifecycleOrder: Record<TestStatus, number> = {
  [TestStatus.Draft]: 0,
  [TestStatus.InReview]: 1,
  [TestStatus.InProgress]: 2,
  [TestStatus.Complete]: 3,

  // deprecated statuses
  [TestStatus.InTesting]: -1,
  [TestStatus.InDesign]: -1
}

export enum TestTypes {
  Template='Template',
  UserDiscovery='User Discovery',
  CompetitiveOverview='Competitive Overview',
  ProductConceptTesting='Product Concept Testing',
  Comparison='Comparison',
  ProductDiscovery='Product Discovery',
  FeaturePrioritization='Feature Prioritization',
  UXRefinement='UX Refinement',
  UsabilityTesting='Usability Testing'
}

export interface AudienceReference {
  title: string,
  id: string
}
export interface Test {
  _id: ObjectID,
  id: number,
  createdAt: number,
  updatedAt: number,
  payload: TestPayload
  created_by: UserReference
}

export enum QuestionType {
  SingleSelect = 'single-select',
  MultiSelect = 'multi-select',
  Rank = 'rank',
  TextArea = 'textarea',
  Number = 'number',
  Variant = 'variant',
  Rate = 'rate',
  Matrix = 'matrix-likert',
  Description = 'description',

  // Deprecated TODO: Do we want to remove these?
  RateOnly = 'rate-only',
  Task = 'task',
  Verbal = 'verbal'
}

export enum SliceType {
  DEFAULT = 'Default',
  GENDER = 'Gender',
  AGE = 'Age',
  EDUCATION = 'Education',
  STANDARD_EMPLOYMENT = 'Employment',
  STANDARD_HHI_US = 'HHI',
  REGION = 'Region',
  RACE_AND_HISPANIC_ORGIN = 'Race and Hispanic Origin',
  RACE = 'Race',
  HISPANIC_ORIGIN = 'Hispanic Origin',
  DEVICE = 'Device'
}

export interface QuestionScaleGrouping {
  label: string
  percentage: {
    max: number
    min: number
  }
}

export interface QuestionScaleProperties {
  maxLabel: string,
  minLabel: string,
  minRating: number,
  maxRating: number
  naOption?: boolean
}

export interface QuestionScale {
  properties: QuestionScaleProperties,
  dimension?: string,
  grouping: QuestionScaleGrouping[]
}

export type Question = QuestionWithFields | QuestionBase
interface QuestionBase {
  id: string,
  label: string,
  custom?: boolean,
  explain?: boolean,
  edited?: boolean
  otherOpt?: boolean,
  naOpt?: boolean,
  lockOptions?: boolean,
  splitTestCapable?: boolean
  required?: boolean,
  uneditable?: boolean,
  updateWithInstructions?: boolean,
  scale?: QuestionScale,
  answers?: {label: string}[],
  questionType: QuestionType
}

export interface QuestionWithFields extends QuestionBase {
  templateFields: TemplateField[]
}

export interface QuestionPart {
  type: string
  field?: TemplateField
  text?: string
}

export interface Template {
  id: number,
  title?: string,
  questions: Question[],
  template_fields: Record<string, TemplateField>
  created_by: string
  featured: boolean
  hidden?: boolean
}

export interface TemplateField {
  type?: string,
  label?: string,
  placeholder?: string,
  placement?: 'inline' | 'prepend' | 'append' | 'min-rating' | 'max-rating' | 'min-label' | 'max-label',
  id?: string,
  options?: number[],
  value?: TemplateValue['value']
  lockOptions?: boolean
}

export interface UserReference {
  id: string,
  uri?: string,
  image?: string,
  name?: string
}

// { firstname: string, lastname: string, email: string, userid: string }

export interface TemplateValue {
  value?: string | string[] | number
  variant?: boolean
  variantLabel?: string
  asset?: string
  lockOptions?: boolean
}

type UserId = string // mongo objectid

export enum Rating {
  Good = 'good',
  Bad = 'bad',
  None = 'none'
}

export interface Feedback {
  rating: Rating,
  text: string
}

export interface TestPayload {
  segment?: AudienceReference,
  title?: string,
  objective?: string,
  decisionContext?: string,
  customerQuestions: string,
  formtype: string,
  description: string,
  metric: string,
  date: string,
  status: TestStatus,
  testType: string,
  directory: string,
  amnotes: string,
  hidden: boolean,
  people: number,
  auto_survey: boolean,
  assets: Assets,
  workspace: Relation,
  lastSubmittedAt?: number,
  design_requests?: number[],
  prototypes: Record<string, Prototype>,
  activeviewid?: string,
  answer?: string,
  additional_info?: string,
  feedback?: Record<UserId, Feedback>
  tags?: string,
  comments?: Record<string, MongoComment>,
  qualNotable?: QualNotable[],
  testTemplateId?: number,
  renderedTemplate?: Question[],
  templateFieldValues?: Record<string, TemplateValue> | null,
  comparison_type?: string,
  testTemplateLaunched?: boolean,
  completedAt?: number,
  relatedIds?: Array<string | number>,
  prioritizedAt?: number,
  hasFilterJob?: true,
  cloned?: 'true', // deprecated && TODO:COLLIN this should be a boolean but it was being saved as 'true' string all this time
  groupType?: string, // deprecated (possibly an accident)
}

export interface QualNotable {
  questionNumber: number,
  questionText: string
}
export interface MongoComment {
  userid: string
  firstname: string
  lastname: string
  comment: string
  clientid: string
  cardid: string
  tagged_user_ids?: string[]
}

export interface Report {

}

export interface SlackIntegrationConfig {
  scope: string,
  team: string,
  team_id: string,
  user_id: string,
  channel: string,
  channel_id: string,
  token: string,
  hook: string,
  configuration_url: string
}

export interface Relation {
  id: string
}

export interface GeneralConfig {
  setimentBeta: number | boolean,
  dedup: number | boolean,
  audienceBuilder: number | boolean,
  insightsFinder: boolean,
  minResponsePerCategory: number,
  pValue: number,
  alphaClassic: boolean
}

export enum AudienceTypes {
  USAdults = 'US Adults',
  GenPop = 'General Population'
}

export interface Audiences {
  [id: string]: Audience;
}

export interface Audience {
  id: string
  label: string,
  description: string,
  createdAt: number
  legacy: boolean
  criteria: Record<string, any>
  title: string
  segid: string
  demoQualifies: boolean
  accountId: string
  payload: {
    account_id: string
    name: string
  }
}

export interface PrequalOption {
  title: string,
  precodes: string[]
}
export interface Prequalification {
  name: string
  lucidKey: string
  button_text: string // eslint-disable-line
  type: 'slider' | 'checkbox' | 'checklist'
  step?: number
  init?: {
    min: number
    max: number
  }
  default?: {
    min: number
    max: number
  }
  range?: {
    min: number,
    max: number
  },
  options: PrequalOption[]
}

export interface Asset {
  title: string,
  asset_type: string,
  url: string
  custom_question?: boolean,
  type?: string
  analysis?: string,
  alphaAnalysis?: string
  session_id?: string
  originalQuestion?: string,
  hostedCsv?: string,
  rawData?: string,
  surveyId?: string,
  auto_survey?: boolean,
  question_type?: string
  prototypes?: any[], // TODO:COLLIN figure out what type these are
  surveyIdInt?: string,
  questionId?: number,
  groupType?: number,
  charts?: Record<string, Chart>
  logger_notes?: string | LoggerNote[]
  loggerNotes?: string | LoggerNote[]
  textResponses?: Record<string, TextResponse>
  img?: string
}

export type Assets = Record<string, Asset>

export interface LoggerNote {
  number_of_actual_insights: number
  answer: string
  question: string
}

export type AssetType = '' | 'mobile' | 'desktop' | 'data' | 'document' | 'textResponses' | 'text-slice' | 'textFrequency' | 'textComparison' | 'audio' | 'video' | 'ut' | 'url' | 'clientupload' | 'dropbox' | 'google'

export interface AssetPrototype {
  url: string
  title: string
}

export enum PrototypeSource {
  Alpha = 'alpha',
  Client = 'client'
}
export interface Prototype {
  asset_type: AssetType | string
  title: string
  comparisonPrototype?: string | boolean
  conceptPrototype?: string | boolean
  conceptPrototypeDesignRequest?: string | boolean
  id?: string
  source?: PrototypeSource
  url?: string
  selected?: boolean
  tested?: boolean
}

export interface Chart extends SubAsset {
  originalChartId?: string
}

export interface TextResponse extends SubAsset {
}

export interface SubAsset {
  url: string
  keyInsight: boolean
  alphaAnalysis: string
  analysis?: string
  title: string
  type?: string
  lucidKey?: string
  asset_type?: 'text-slice' | 'viz-slice'
  slice?: string
}
export interface Section {
  title: string,
  description: string,
  questions: string[]
}

export interface UserSession {
  auth_type: string,
  uid: string,
  firstname: string,
  lastname: string,
  admin: boolean,
  tester?: string,
  tester_task_types: string[],
  payment_details: any,
  email: string,
  primary_account: string,
  org_admin_access: string[],
  whitelist: Permission[],
  org_id: string,
  account_id: string,
  token: string
}

export type PermissionLevel = 'workspace_admin' | 'organization_admin' | 'basic_user'

export interface Permission {
  name: string,
  icon: string,
  id: string,
  org_id: string,
  permission: PermissionLevel
}

export enum ValidationErrors {
  MissingTitleError = 'Uploads must all have a title',
  MissingUrlError = 'Uploads must all have a URL',
  InvalidUrlError = 'Uploads must all have a valid URL'
}

export enum ErrorCodes {
  InternalServerError='INTERNAL_SERVER_ERROR',
  Forbidden='FORBIDDEN',
  Unauthorized='UNAUTHORIZED',
  BadUserInput='BAD_USER_INPUT',
  MongoError='MONGO_ERROR',
  PostgresError='POSTGRESS_ERROR',
  RedisError='REDIS_ERROR'
}

export interface Notification {
  content: string,
  text_content: string,
  url: string,
  target_blank?: boolean,
  created_at?: string
}

/* RR Types */
export interface RRAnswerOption {
  text: string
  shuffle: boolean
  followup: boolean
  url?: {
    title: string
    url: string
  }
  image?: {
    title: string
    url: string
  } | boolean | string
  // eslint-disable-next-line camelcase
  set_tag: string[]
  label: string
  herring?: boolean
  // eslint-disable-next-line camelcase
  var_tag?: string
  qualification_impact?: 'disqualifies' | 'qualifies' | null
}

export interface RRSurvey {
  status: string,
  name: string,
  // eslint-disable-next-line camelcase
  auto_source: boolean,
  // eslint-disable-next-line camelcase
  auto_cadence: {
    enabled: boolean,
    interval: string,
    limit: number | false
  },
  // eslint-disable-next-line camelcase
  active_views: [],
  // eslint-disable-next-line camelcase
  card_id: string, // maybe?
  // eslint-disable-next-line camelcase
  auto_survey: true,
  // eslint-disable-next-line camelcase
  client_id: string,
  // eslint-disable-next-line camelcase
  client_name: string | false, // is false possible, really?
  mode: 'quant', // allow qual?
  // eslint-disable-next-line camelcase
  preview_audience: false,
  quota: number, // 400
  segment: {
    id: string
  },
  context: '',
  startURL: '',
  mobileTest: false,
  mobileUID: false,
  // eslint-disable-next-line camelcase
  submitted_by: string,
  questions: RRQuestion[],
  uid: string,
  dedupe: {
    enabled: boolean,
    id: string,
    type: DedupeType
  },
  testTemplateId: number | false
}

export interface RRQuestion {
  type: QuestionType
  instructions?: string
  // eslint-disable-next-line camelcase
  instruction_qual?: string
  prequal?: {
    // eslint-disable-next-line camelcase
    tags_set: string[]
    completed: number
  },
  content: string
  custom?: boolean
  answers?: RRAnswerOption[]
  otherOpt?: boolean
  limitOpt?: boolean
  explainOpt?: boolean
  naOpt?: boolean
  followup?: boolean
  herringOpt?: boolean
  maximum?: number
  // eslint-disable-next-line camelcase
  post_answers_instructions_qual?: string
  scale?: QuestionScale,
  // eslint-disable-next-line camelcase
  scale_number?: {
    properties: {
      minValue: number
      maxValue: number
      naOption: boolean
    }
  }
  // eslint-disable-next-line camelcase
  viz_version: number
  viz: {
    version: number
    title: string|false
    subtitle: string|false
    sort: string|false
    segment?: string|false
    segmentBy?: number
    inverse: string|false
  }
  assets: {
    images: any[]
    urls: {
      title: string
      url: string
      forceOpen: boolean
    }[]
  }
  edited?: boolean
  meta: {
    // eslint-disable-next-line camelcase
    client_id: string,
    // eslint-disable-next-line camelcase
    card_id: string
  }
}
